import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Obstacle } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { METERS_TO_MILLIMETERS_FACTOR } from '@/modules/canvas/factories/element';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';

interface Props {
  selectedObstacles: Obstacle[];
}

/** @description Only boxes and walls have a height that can be edited. */
const hasEditableHeight = ({ appearance, segments }: Obstacle): boolean =>
  (appearance === 'BOX' || appearance === 'WALL') && segments[0].height !== undefined;

function HeightComponent({ selectedObstacles }: Props) {
  const { t } = useTranslation();
  const fddmSvc: IFddmService = useMemo(
    () => FddmServiceFactory.getInstance().createFddmService(),
    [],
  );

  // Shown when any selected obstacle has an editable height, only editable when all of them do
  const heightObstacles = useMemo(
    () => selectedObstacles.filter(hasEditableHeight),
    [selectedObstacles],
  );
  const isPartialSelection = heightObstacles.length !== selectedObstacles.length;

  const heights = useMemo(
    () =>
      heightObstacles.flatMap(({ segments: [{ height }] }) =>
        height === undefined ? [] : [height],
      ),
    [heightObstacles],
  );

  // Compared in whole mm, so float noise doesn't show up as 'Mixed'
  const heightMm = useMemo(
    () =>
      getCommonNumber(heights.map((height) => Math.round(height * METERS_TO_MILLIMETERS_FACTOR))),
    [heights],
  );

  const onChange = useCallback(
    (newValue: number) => {
      if (isPartialSelection) return;

      const valueInMeter = newValue / METERS_TO_MILLIMETERS_FACTOR;

      fddmSvc.updateObstacles(
        selectedObstacles.map((obstacle) => ({
          ...obstacle,
          segments: minItemsArrayUtils.mapAtLeast1(obstacle.segments, (segment) => ({
            ...segment,
            height: valueInMeter,
          })),
        })),
      );
    },
    [isPartialSelection, selectedObstacles, fddmSvc],
  );

  if (heightObstacles.length === 0) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.wall.height'>
      <DisabledReasonTooltip
        reasonKey={
          isPartialSelection
            ? 'interface:properties.obstacle.partial_selection.only_box_or_wall'
            : undefined
        }
      >
        <NumberInput
          // undefined (not false) keeps NumberInput's own read-only-mode check
          disabled={isPartialSelection ? true : undefined}
          value={heightMm}
          errorType='floating'
          unit={t('interface:settings.units.mm', 'mm')}
          minValue={0}
          onChange={onChange}
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

const Height = memo(HeightComponent);

export default Height;
